<template>
  <Sidebar
    v-model:visible="internalVisible"
    position="right"
    class="spm-table-api-controls-panel"
    :modal="true"
    :dismissable="true"
  >
    <!-- Header slot -->
    <template #header>
      <span class="spm-table-api-controls-panel__title">
        {{ t('spmTableApi.controlsPanel.title') }}
      </span>
    </template>

    <div class="spm-table-api-controls-panel__container">
      <!-- Content -->
      <div class="spm-table-api-controls-panel__content">
        <!-- Filters section -->
        <div
          v-if="filterableFields.length > 0"
          class="spm-table-api-controls-panel__section"
        >
          <div class="spm-table-api-controls-panel__section-header">
            <span class="spm-table-api-controls-panel__section-title">
              <i class="fa-regular fa-filter" />
              {{ t('spmTableApi.controlsPanel.filters') }}
            </span>

            <!-- Add filter dropdown -->
            <Dropdown
              v-model="selectedFilterToAdd"
              :options="availableFiltersGrouped"
              option-group-label="label"
              option-group-children="items"
              option-label="label"
              option-value="name"
              :placeholder="t('spmTableApi.controlsPanel.addFilter')"
              class="spm-table-api-controls-panel__add-dropdown"
              :disabled="availableFiltersCount === 0"
              @change="onAddFilter"
            >
              <template #optiongroup="{ option }">
                <div
                  v-if="option.kind === 'relation-group'"
                  class="spm-table-api-controls-panel__option-group"
                >
                  <i class="fa-regular fa-link" />
                  <span>{{ option.label }}</span>
                </div>
              </template>

              <template #option="{ option }">
                <div
                  class="spm-table-api-controls-panel__option"
                  :class="`spm-table-api-controls-panel__option--${option.kind}`"
                >
                  <i
                    v-if="option.kind === 'relation-sub'"
                    class="fa-regular fa-link"
                  />
                  <span>{{ option.displayLabel }}</span>
                </div>
              </template>

              <template #value>
                <span
                  class="spm-table-api-controls-panel__add-btn"
                  :class="{ 'spm-table-api-controls-panel__add-btn--disabled': availableFiltersCount === 0 }"
                >
                  <i class="fa-regular fa-plus" />
                  {{ t('spmTableApi.controlsPanel.addFilter') }}
                </span>
              </template>
            </Dropdown>
          </div>

          <!-- Active filters list -->
          <div
            v-if="activeFilterFields.length > 0"
            class="spm-table-api-controls-panel__filters-list"
          >
            <SpmFilterInput
              v-for="field in activeFilterFields"
              :key="field.name"
              :field-config="field"
              :model-value="localFilters.get(field.name) ?? null"
              :label="field.label || field.name"
              @update:model-value="updateFilter(field.name, $event)"
              @remove="removeFilter(field.name)"
            />
          </div>

          <!-- Empty filters state -->
          <div
            v-else
            class="spm-table-api-controls-panel__empty-filters"
          >
            <p>{{ t('spmTableApi.controlsPanel.noActiveFilters') }}</p>
          </div>
        </div>

        <!-- Sorting section -->
        <div
          v-if="sortableFields.length > 0"
          class="spm-table-api-controls-panel__section"
        >
          <div class="spm-table-api-controls-panel__section-header">
            <span class="spm-table-api-controls-panel__section-title">
              <i class="fa-regular fa-arrow-up-arrow-down" />
              {{ t('spmTableApi.controlsPanel.sorting') }}
            </span>
          </div>
          <div
            v-if="sortingDisabled"
            class="spm-table-api-controls-panel__sorting-disabled"
          >
            <i class="fa-regular fa-circle-info" />
            <span>{{ t('spmTableApi.controlsPanel.sortingDisabledHint') }}</span>
          </div>
          <SpmSortInput
            v-else
            v-model="localSort"
            :sortable-fields="sortableFields"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="spm-table-api-controls-panel__footer">
        <SpmButton
          :label="t('spmTableApi.controlsPanel.reset')"
          class-style="p-button p-button-secondary"
          @click="resetChanges"
        />
        <SpmButton
          :label="t('spmTableApi.controlsPanel.apply')"
          class-style="p-button p-button-success"
          :disabled="!hasChanges"
          @click="applyChanges"
        />
      </div>
    </div>
  </Sidebar>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  PropType,
  ref,
  watch,
} from 'vue';
import Sidebar from 'primevue/sidebar';
import Dropdown from 'primevue/dropdown';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { useI18n } from 'vue-i18n';
import type { SpmFieldConfig, SpmActiveSort } from './types';
import type { SpmActiveFilter } from './filters';
import { SpmFilterInput, SpmSortInput } from './filters/components';

export default defineComponent({
  name: 'SpmTableApiControlsPanel',

  components: {
    Sidebar,
    Dropdown,
    SpmButton,
    SpmFilterInput,
    SpmSortInput,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    sortableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      default: () => [],
    },

    filterableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      default: () => [],
    },

    // Current active filters from parent
    activeFilters: {
      type: Object as PropType<Map<string, SpmActiveFilter>>,
      default: () => new Map(),
    },

    // Current active sort from parent
    activeSort: {
      type: Object as PropType<SpmActiveSort | null>,
      default: null,
    },

    // Désactive la section "Tri" du panel quand le back tourne en mode
    // pagination optimisée (ORDER BY retiré). Les filtres restent dispos
    // car ils continuent d'être appliqués par le back. Cf. /stats/widget-data
    // avec includeCount: false ou auto-bascule au-delà de 50000 lignes.
    sortingDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'apply', 'reset'],

  setup(props, { emit }) {
    interface FilterDropdownOption {
      name: string;
      label: string;
      displayLabel: string;
      kind: 'local' | 'relation-base' | 'relation-sub';
      sourceField?: string;
    }

    interface FilterDropdownGroup {
      kind: 'local-group' | 'relation-group';
      label: string;
      items: FilterDropdownOption[];
    }

    const { t } = useI18n();

    // Two-way binding for visible prop
    const internalVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => emit('update:visible', value),
    });

    // Local copy of filters for editing (before apply)
    const localFilters = ref<Map<string, SpmActiveFilter>>(new Map());

    // Local copy of sort for editing (before apply)
    const localSort = ref<SpmActiveSort | null>(null);

    // Track which filter fields are currently shown (added by user)
    const shownFilterFields = ref<Set<string>>(new Set());

    // Selected filter to add (for dropdown)
    const selectedFilterToAdd = ref<string | null>(null);

    // Computed: fields that are currently shown as filters
    const activeFilterFields = computed(() => props.filterableFields.filter(
      (field) => shownFilterFields.value.has(field.name),
    ));

    const toLabel = (value: string): string => value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const normalizeRelationSubLabel = (
      rawLabel: string,
      sourceLabel: string,
      optionName: string,
    ): string => {
      const sourcePrefix = `${sourceLabel} - `;
      if (rawLabel.startsWith(sourcePrefix)) {
        return rawLabel.slice(sourcePrefix.length).trim();
      }
      if (rawLabel.includes(' - ')) {
        return rawLabel.split(' - ').slice(1).join(' - ').trim();
      }
      if (optionName.includes('__rel__')) {
        return toLabel(optionName.split('__rel__')[1]);
      }
      return rawLabel;
    };

    const availableFiltersGrouped = computed<FilterDropdownGroup[]>(() => {
      const availableFields = props.filterableFields
        .filter((field) => !shownFilterFields.value.has(field.name));

      const relationEntries = availableFields
        .filter((field) => !!field.relationFilterConfig);
      const relationBySource = relationEntries.reduce(
        (accumulator: Record<string, SpmFieldConfig[]>, field) => {
          const sourceField = field.relationFilterConfig?.sourceField;
          if (!sourceField) return accumulator;
          if (!accumulator[sourceField]) {
            accumulator[sourceField] = [];
          }
          accumulator[sourceField].push(field);
          return accumulator;
        },
        {},
      );

      const relationSourceFields = new Set(Object.keys(relationBySource));
      const localOptions: FilterDropdownOption[] = [];
      const relationGroups: FilterDropdownGroup[] = [];

      availableFields.forEach((field) => {
        const fieldLabel = field.label || field.name;
        if (field.relationFilterConfig) return;
        if (relationSourceFields.has(field.name)) return;

        localOptions.push({
          name: field.name,
          label: fieldLabel,
          displayLabel: fieldLabel,
          kind: 'local',
        });
      });

      Object.keys(relationBySource)
        .sort((sourceA, sourceB) => {
          const labelA = props.filterableFields.find((field) => field.name === sourceA)?.label || sourceA;
          const labelB = props.filterableFields.find((field) => field.name === sourceB)?.label || sourceB;
          return labelA.localeCompare(labelB);
        })
        .forEach((sourceField) => {
          const sourceConfig = props.filterableFields.find((field) => field.name === sourceField);
          const sourceLabel = sourceConfig?.label || sourceField;
          const groupItems: FilterDropdownOption[] = [];

          if (sourceConfig && !shownFilterFields.value.has(sourceConfig.name)) {
            groupItems.push({
              name: sourceConfig.name,
              label: sourceLabel,
              displayLabel: sourceLabel,
              kind: 'relation-base',
              sourceField,
            });
          }

          relationBySource[sourceField]
            .sort((fieldA, fieldB) => (fieldA.label || fieldA.name).localeCompare(fieldB.label || fieldB.name))
            .forEach((field) => {
              const rawLabel = field.label || field.name;
              groupItems.push({
                name: field.name,
                label: rawLabel,
                displayLabel: normalizeRelationSubLabel(rawLabel, sourceLabel, field.name),
                kind: 'relation-sub',
                sourceField,
              });
            });

          if (groupItems.length > 0) {
            relationGroups.push({
              kind: 'relation-group',
              label: sourceLabel,
              items: groupItems,
            });
          }
        });

      localOptions.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));

      const groups: FilterDropdownGroup[] = [];
      if (localOptions.length > 0) {
        groups.push({
          kind: 'local-group',
          label: '',
          items: localOptions,
        });
      }
      groups.push(...relationGroups);
      return groups;
    });

    const availableFiltersCount = computed(() => availableFiltersGrouped.value
      .reduce((total, group) => total + group.items.length, 0));

    // Sync local state when panel opens
    watch(() => props.visible, (isVisible) => {
      if (isVisible) {
        // Copy current filters to local state
        localFilters.value = new Map(props.activeFilters);
        localSort.value = props.activeSort ? { ...props.activeSort } : null;
        // Show fields that have active filters
        shownFilterFields.value = new Set(props.activeFilters.keys());
      }
    });

    const closePanel = () => {
      emit('update:visible', false);
    };

    /**
     * Add a filter field to the shown list
     */
    const onAddFilter = () => {
      if (selectedFilterToAdd.value) {
        shownFilterFields.value.add(selectedFilterToAdd.value);
        selectedFilterToAdd.value = null;
      }
    };

    /**
     * Update a filter in local state
     */
    const updateFilter = (fieldName: string, filter: SpmActiveFilter | null) => {
      if (filter) {
        localFilters.value.set(fieldName, filter);
      } else {
        localFilters.value.delete(fieldName);
      }
    };

    /**
     * Remove a filter from local state and hide it
     */
    const removeFilter = (fieldName: string) => {
      localFilters.value.delete(fieldName);
      shownFilterFields.value.delete(fieldName);
    };

    const applyChanges = () => {
      // En mode pagination optimisée, le back ignore l'ORDER BY ; on ne
      // remonte donc pas le `localSort` pour éviter un état fantôme
      // (chip de tri visible mais sans effet).
      const sortToEmit = props.sortingDisabled ? null : localSort.value;
      emit('apply', localFilters.value, sortToEmit);
      closePanel();
    };

    const resetChanges = () => {
      // Clear local state
      localFilters.value.clear();
      localSort.value = null;
      emit('reset');
      closePanel();
    };

    // Check if there are any changes to apply (deep comparison)
    const hasChanges = computed(() => {
      // Convert Maps to arrays for JSON comparison
      const localFiltersArray = Array.from(localFilters.value.entries());
      const propsFiltersArray = Array.from(props.activeFilters.entries());

      const filtersChanged = JSON.stringify(localFiltersArray) !== JSON.stringify(propsFiltersArray);
      const sortChanged = JSON.stringify(localSort.value) !== JSON.stringify(props.activeSort);

      return filtersChanged || sortChanged;
    });

    return {
      t,
      internalVisible,
      localFilters,
      localSort,
      activeFilterFields,
      availableFiltersGrouped,
      availableFiltersCount,
      selectedFilterToAdd,
      onAddFilter,
      updateFilter,
      removeFilter,
      applyChanges,
      resetChanges,
      hasChanges,
    };
  },
});
</script>

<style lang="scss">
.spm-table-api-controls-panel {
  width: 380px !important;

  .p-sidebar-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #eee;

    // Align header content to the left
    .p-sidebar-header-content {
      width: 100%;
    }
  }

  .p-sidebar-content {
    padding: 0 !important;
    height: 100%;
  }

  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #ffffff;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }

  &__content {
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1.25rem;
  }

  &__section {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
  }

  &__section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    i {
      font-size: 0.875rem;
      color: #999;
    }
  }

  &__add-dropdown {
    .p-dropdown {
      border: none;
      background: transparent;
      box-shadow: none;

      &:not(.p-disabled):hover,
      &:not(.p-disabled).p-focus {
        border: none;
        box-shadow: none;
      }

      .p-dropdown-label {
        padding: 0;
      }

      .p-dropdown-trigger {
        display: none;
      }
    }
  }

  &__option-group {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #7a8595;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 0.2rem 0;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.95rem;
    color: #2f3a4b;
    line-height: 1.35;
    padding: 0.2rem 0;

    &--relation-base {
      font-weight: 600;
    }

    &--relation-sub {
      padding-left: 1rem;
      font-size: 0.9rem;
      color: #536176;
    }
  }

  &__add-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #94c840;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      color: #7ab030;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;

      &:hover {
        color: #ccc;
      }
    }

    i {
      font-size: 0.75rem;
    }
  }

  &__filters-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__empty-filters {
    padding: 1.5rem;
    text-align: center;
    background: #fafafa;
    border-radius: 6px;
    border: 1px dashed #ddd;

    p {
      margin: 0;
      color: #999;
      font-size: 0.8125rem;
    }
  }

  &__sorting-disabled {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 0.75rem 1rem;
    background: #fff8e6;
    border: 1px solid #f0d97d;
    border-radius: 6px;
    color: #6b5b1a;
    font-size: 0.8125rem;
    line-height: 1.4;

    i {
      margin-top: 0.125rem;
      flex-shrink: 0;
    }
  }

  &__footer {
    display: flex;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-top: 1px solid #eee;

    .p-button {
      flex: 1;
    }
  }
}
</style>
